import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { ConflictImage, InspectionImageCreate, InspectionImageListItem } from "../../_objects/inspection-image";
import { InspectionImageStateService } from "../../inspection-data-management/inspection-images-state.service";
import { ConflictAction } from "../../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.component";
import { ImageConflictDialogService } from "../../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.service";
import { ImageConflictResponse } from "../responses/inspection-image.response";

@Injectable({
  providedIn: "root"
})
export class ImageConflictResolverService {
  private conflictResolved$: Subject<InspectionImageCreate[]> = new Subject<InspectionImageCreate[]>();
  private inspectionImages: InspectionImageListItem[] = [];

  constructor(
    private imageConflictDialogService: ImageConflictDialogService,
    private inspectionImageStateService: InspectionImageStateService
  ) {
    this.inspectionImageStateService.inspectionImages$.subscribe((image) => (this.inspectionImages = image));
  }

  public resolveConflicts(conflictedImages: ImageConflictResponse[], imagesToCreate: InspectionImageCreate[]) {
    const conflictImages = conflictedImages.map((conflicted) => {
      const image = this.retreiveCreatedImage(imagesToCreate, conflicted);
      const existingImage = this.retrieveExistingImage(conflicted);
      const filePath = "path" in image.file ? (image.file.path as string) : "";
      return new ConflictImage(image?.file, image.file?.name, filePath, image, image.deviceFileUri, existingImage?.thumbFilePath, false, null, null);
    });
    this.openConflictDialog(conflictImages);

    return this.conflictResolved$;
  }

  private retrieveExistingImage(conflicted: ImageConflictResponse) {
    return this.inspectionImages.find((existing) => existing.id == conflicted.imageId);
  }

  private retreiveCreatedImage(imagesToCreate: InspectionImageCreate[], conflicted: ImageConflictResponse) {
    // we use the folder name here since the images have not been created yet, and the location name is not necessary the location that gonna be assign
    return imagesToCreate.find((imageCreation) => {
      // The following checks have been broken down to facilitate debugging process when troubleshooting
      const hasSameFileName = imageCreation.fileName === conflicted.fileName || imageCreation.fileName == conflicted.originalFileName;
      const hasSameFolderName = imageCreation.folderName == conflicted.folderName;
      const isConflictingImage = hasSameFileName && hasSameFolderName;
      return isConflictingImage;
    });
  }

  private openConflictDialog(conflictList: ConflictImage[]): void {
    const dialogRef = this.imageConflictDialogService.open(conflictList);
    const resolved: InspectionImageCreate[] = [];
    dialogRef.componentInstance.dialogAction.subscribe((conflictAction: ConflictAction) => {
      if (conflictAction.applyToAll) {
        this.imageConflictDialogService.close();
        this.conflictResolved$.next([...resolved, ...conflictList.map((image) => this.resolveImageConflict(image, conflictAction))]);
      } else {
        const image = this.resolveImageConflict(conflictAction.image, conflictAction);
        resolved.push(image);
        this.imageConflictDialogService.nextConflictImage();
        if (this.imageConflictDialogService.conflictImageList.length == 0) {
          this.conflictResolved$.next(resolved);
        }
      }
    });

    dialogRef.componentInstance.cancelAction.subscribe((_) => {
      this.imageConflictDialogService.close();
      this.conflictResolved$.next([]);
    });
  }

  private resolveImageConflict(image: ConflictImage, conflictAction: ConflictAction): InspectionImageCreate | null {
    return { ...image.inspectionImageCreate, uploadOption: conflictAction.uploadOption } as InspectionImageCreate;
  }
}
